import { SectionWrapper } from '../../../../components/layout/section-wrapper';
import { useGetEosConsumptionBySourceQuery } from '@client/api';
import { toNumberString } from '@client/toolkit';
import { useDashboardFilters } from '../../../dashboard/context/dashboard-filters.context';
import { useMemo, useState } from 'react';
import { useTagLabel } from '../../../../utils/eos/getTagLabel';
import {
  SeriesDataPoint,
  StackedBarChart,
  StackedBarChartDataPoint,
} from '../../../../components/charts/stacked-bar-chart';
import { useChartResolutionDateFormat } from '../../../../components/charts/hooks/useChartLabelFormat';
import { getSeriesColor } from '../../../../utils/eos/getSeriesColor';
import { ChartLegend } from '../../../../components/charts/types';
import { BasicChartTooltipProps, BasicChartTooltip } from '../../../../components/charts/tooltip';
import { ChartSectionState } from '../../../../components/charts/chart-section-state';
import { useTranslation } from 'react-i18next';
import { utcToZone } from '../../../../utils/date/utcToZone';
import { useEosSourcesFilterContext } from '../../../../components/eos/filters/eos-sources-filter';
import { getSourceDisplayName } from '../../reporting-consumption-energy-type/components/consumption-source-tag';

type AggregationBucket = {
  key: string;
  value: number;
};

type AggregationTag = {
  key: string;
  unit?: string | null;
  total?: number;
  buckets?: AggregationBucket[] | null;
};

type AggregationBySource = {
  source: string;
  total?: number;
  tags?: AggregationTag[] | null;
};

type ConsumptionBySourceResponseLocal = {
  resolution?: string | null;
  sources?: {
    total?: number;
    aggregations?: AggregationBySource[] | null;
  } | null;
  seriesTags?: string[];
  series?: SeriesTagResponse[] | null;
};

type SeriesTagResponse = {
  tag: string;
  unit?: string | null;
  buckets?: { key: string; value: number }[] | null;
};

function mapAggregationsToBars(aggregations: AggregationBySource[] = []): {
  sources: string[];
  buckets: StackedBarChartDataPoint[];
} {
  const sourcesSet = new Set<string>();
  const bucketsByTimestamp = new Map<string, StackedBarChartDataPoint>();

  aggregations.forEach((aggregation) => {
    const source = aggregation.source;
    sourcesSet.add(source);

    aggregation.tags?.forEach((tagAggregation) => {
      tagAggregation.buckets?.forEach((bucket) => {
        const existing = bucketsByTimestamp.get(bucket.key) ?? { key: bucket.key };
        const currentValue = Number(existing[source] ?? 0);
        existing[source] = currentValue + bucket.value;
        bucketsByTimestamp.set(bucket.key, existing);
      });
    });
  });

  const buckets = Array.from(bucketsByTimestamp.values()).sort((a, b) => a.key.localeCompare(b.key));
  const sources = Array.from(sourcesSet).sort((a, b) => a.localeCompare(b));

  return { sources, buckets };
}

function getMappedResponse(data?: unknown) {
  if (!data) {
    return {
      resolution: undefined,
      sources: [] as string[],
      sourceTotals: {} as Record<string, number>,
      exportTotals: {} as Record<string, number>,
      buckets: [] as StackedBarChartDataPoint[],
      seriesTags: [] as string[],
      series: [] as SeriesTagResponse[],
    };
  }

  const response = data as ConsumptionBySourceResponseLocal;
  const aggregations = response.sources?.aggregations;
  const mapped = aggregations ? mapAggregationsToBars(aggregations) : null;
  const sourceTotals = Object.fromEntries((aggregations ?? []).map((a) => [a.source, a.total ?? 0]));
  const exportTotals = Object.fromEntries(
    (aggregations ?? [])
      .map((a) => [a.source, a.tags?.find((t) => t.key === 'export')?.total ?? 0] as const)
      .filter(([, total]) => total > 0),
  );

  return {
    resolution: response.resolution,
    sources: mapped?.sources ?? [],
    sourceTotals,
    exportTotals,
    buckets: mapped?.buckets ?? [],
    seriesTags: response.seriesTags ?? [],
    series: response.series ?? [],
  };
}

export function useConsumptionBySourceQuery() {
  const getTagLabel = useTagLabel();
  const { sources: selectedSources } = useEosSourcesFilterContext();
  const { selectedSpaceIds, selectedDateFromIso, selectedDateToIso } = useDashboardFilters();
  const { data, isLoading, isError } = useGetEosConsumptionBySourceQuery(
    {
      from: selectedDateFromIso,
      to: selectedDateToIso,
      spaceIds: selectedSpaceIds,
      sources: selectedSources,
    },
    {
      skip: !selectedDateFromIso || !selectedDateToIso,
    },
  );
  const {
    resolution,
    sources = [],
    sourceTotals = {},
    exportTotals = {},
    buckets = [],
    seriesTags = [],
    series = [],
  } = getMappedResponse(data);

  const legends = useMemo(() => {
    const barLegends = sources.map<ChartLegend>((source) => ({
      id: source,
      label: getSourceDisplayName(source),
      color: getSeriesColor(source),
    }));

    const seriesLegends = seriesTags
      .filter((tag) => tag !== 'energyPrice')
      ?.map<ChartLegend>((tag) => ({
        id: tag,
        label: getTagLabel(tag),
        color: getSeriesColor(tag),
      }));

    return { barLegends, seriesLegends };
  }, [sources, seriesTags, getTagLabel]);

  const seriesData = useMemo<SeriesDataPoint[]>(() => {
    if (!series) return [];
    return series.map((item) => {
      const withYAxis = item.tag.includes('energyPrice') || item.tag.includes('temperatureOut');
      return {
        key: item.tag,
        unit: item.unit!,
        label: getTagLabel(item.tag),
        yAxis: withYAxis,
        reverseAxis: item.tag.includes('temperatureOut'),
        minSpan: item.tag.includes('energyPrice') ? 1 : undefined,
        data: item.buckets?.map((point) => ({ key: point.key, value: point.value })) ?? [],
      } satisfies SeriesDataPoint;
    });
  }, [series, getTagLabel]);

  return {
    resolution,
    data: buckets ?? [],
    seriesData: seriesData ?? [],
    sources,
    sourceTotals,
    exportTotals,
    legends,
    isLoading,
    isError,
  };
}

export function ConsumptionBySourceGraphSection() {
  const { t } = useTranslation('eos');
  const [activeSeries, setActiveSeries] = useState<string[]>(['temperatureOut']);

  const { data, sources, seriesData, legends, resolution, isLoading, isError } = useConsumptionBySourceQuery();
  const { rotation, getFormat } = useChartResolutionDateFormat(resolution);

  const handleSetActiveSeries = (seriesId: string) => {
    setActiveSeries((prev) => {
      if (prev.includes(seriesId)) {
        return prev.filter((id) => id !== seriesId);
      }
      return [...prev, seriesId];
    });
  };

  if (isLoading) {
    return <ChartSectionState className="aspect-[10/4]">{t('loading')}</ChartSectionState>;
  }

  if (isError) {
    return <ChartSectionState className="aspect-[10/4]">{t('errorLoadingData')}</ChartSectionState>;
  }

  if (!data || data.length === 0) {
    return <ChartSectionState className="aspect-[10/4]">{t('noDataAvailable')}</ChartSectionState>;
  }

  const renderTooltip = (props: any) => {
    let timeLabel: string = '';
    if (resolution === 'hourly') {
      const date = utcToZone(props.indexValue as string);
      timeLabel = `${date.subtract(1, 'hour').format('HH:mm')} - ${date.format('HH:mm')}`;
    } else {
      timeLabel = getFormat(props.indexValue as string);
    }

    const tooltipProps: BasicChartTooltipProps = {
      label: getSourceDisplayName(props.id as string),
      timeLabel,
      value: toNumberString(props.value as number),
      color: getSeriesColor(props.id as string),
      unit: 'kWh',
    };

    return <BasicChartTooltip {...tooltipProps} />;
  };

  return (
    <SectionWrapper>
      <div className="flex flex-col gap-2 w-full">
        <div className="flex w-full aspect-[10/4]">
          <StackedBarChart
            activeSeries={activeSeries}
            setActiveSeries={handleSetActiveSeries}
            groupMode="grouped"
            keys={sources}
            legendsBar={legends.barLegends}
            legendsSeries={legends.seriesLegends}
            bars={data}
            series={seriesData}
            yLabel="kWh"
            tooltip={renderTooltip}
            xLabelRotation={rotation}
            getXLabelFormat={getFormat}
            getColor={getSeriesColor}
          />
        </div>
      </div>
    </SectionWrapper>
  );
}
